import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Grid,
  Typography,
  Box,
  Card,
  useMediaQuery,
  IconButton,
  Tooltip,
  Modal,
  Button,
  Tabs,
  Tab,
  Paper,
  Stack,
} from "@mui/material";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageIcon from "@mui/icons-material/Image";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import AssignmentIcon from "@mui/icons-material/Assignment";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import InsightsIcon from "@mui/icons-material/Insights";
import VisibilityIcon from "@mui/icons-material/Visibility";

import api, { API_BASE_URL } from "../utils/api";
import ProfileCard from "../components/OwnerDashboard/ProfileCard";
import WorkOrders from "../components/OwnerDashboard/WorkOrders";
import ViolationSummary from "../components/OwnerDashboard/ViolationSummary";
import ArchitecturalRequests from "../components/OwnerDashboard/ArchitecturalRequests";

const OwnerDashboard = () => {
  const [user, setUser] = useState(null);
  const [memberData, setMemberData] = useState(null);
  const [accountDetailsMap, setAccountDetailsMap] = useState({});
  const [propertiesMap, setPropertiesMap] = useState({});
  const [violationTypes, setViolationTypes] = useState([]);
  const [violations, setViolations] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [dashboardSettings, setDashboardSettings] = useState({
    show_violations: true,
    show_work_orders: true,
    show_architectural_requests: true,
    show_documents: true,
  });

  const username = localStorage.getItem("username");
  const isMobile = useMediaQuery("(max-width:600px)");

  const selectedProperty = useMemo(() => {
    if (!selectedAccount) return null;
    return propertiesMap[selectedAccount] || null;
  }, [selectedAccount, propertiesMap]);

  const buildPropertyAddress = (property) => {
    if (!property) return "";

    return [
      property.street_1,
      property.street_2,
      property.city,
      property.state,
      property.postal_code,
    ]
      .filter(Boolean)
      .join(", ");
  };

  const getMemberDisplayName = (member) => {
    if (!member) return "";

    const familiarName = member.FamiliarName?.trim();
    const fullName = `${member.FirstName || ""} ${member.LastName || ""}`.trim();

    return familiarName || fullName || "";
  };

  const getDisplayTitle = (member, property, fallbackAccount = "") => {
    const memberName = getMemberDisplayName(member);
    if (memberName) return memberName;

    if (property?.mailing_name?.trim()) return property.mailing_name.trim();

    if (member?.FullAddress?.trim()) return member.FullAddress.trim();
    if (member?.AlternateAddress?.trim()) return member.AlternateAddress.trim();

    const propertyAddress = buildPropertyAddress(property);
    if (propertyAddress) return propertyAddress;

    return fallbackAccount || "Unknown Account";
  };

  const getDisplaySubtitle = (member, property, fallbackAccount = "") => {
    const memberName = getMemberDisplayName(member);
    const memberAddress =
      member?.FullAddress?.trim() || member?.AlternateAddress?.trim() || "";
    const propertyAddress = buildPropertyAddress(property);

    if (memberName && memberAddress) return memberAddress;
    if (memberName && property?.mailing_name?.trim()) return property.mailing_name.trim();

    if (!memberName && property?.mailing_name?.trim() && propertyAddress) {
      return propertyAddress;
    }

    if (!memberName && property?.mailing_name?.trim()) {
      return "Property Account";
    }

    if (!memberName && memberAddress) {
      return "Property Account";
    }

    if (propertyAddress) {
      return propertyAddress;
    }

    return fallbackAccount ? `Member Code: ${fallbackAccount}` : "Property Account";
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!username) return;

      try {
        const [userRes, typesRes, settingsRes] = await Promise.all([
          api.get(`/users/${username}`),
          api.get(`/getCcrViolationTypes`),
          api.get(`/owner-dashboard-settings`),
        ]);

        setUser(userRes.data);
        setViolationTypes(typesRes.data || []);

        if (settingsRes.data) {
          setDashboardSettings({
            show_violations:
              settingsRes.data.show_violations === undefined
                ? true
                : Boolean(settingsRes.data.show_violations),
            show_work_orders:
              settingsRes.data.show_work_orders === undefined
                ? true
                : Boolean(settingsRes.data.show_work_orders),
            show_architectural_requests:
              settingsRes.data.show_architectural_requests === undefined
                ? true
                : Boolean(settingsRes.data.show_architectural_requests),
            show_documents:
              settingsRes.data.show_documents === undefined
                ? true
                : Boolean(settingsRes.data.show_documents),
          });
        }

        if (userRes.data.AssociatedAccounts?.length === 1) {
          setSelectedAccount(userRes.data.AssociatedAccounts[0]);
        }
      } catch (error) {
        console.error("Error fetching initial dashboard data:", error);
      }
    };

    fetchInitialData();
  }, [username]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const res = await api.get("/properties");

        const map = {};
        (res.data || []).forEach((property) => {
          if (property?.account_id) {
            map[String(property.account_id)] = property;
          }
        });

        setPropertiesMap(map);
      } catch (error) {
        console.error("Error loading properties:", error);
      }
    };

    fetchProperties();
  }, []);

  useEffect(() => {
    const fetchAssociatedAccountDetails = async () => {
      const accounts = user?.AssociatedAccounts || [];
      if (!accounts.length) {
        setAccountDetailsMap({});
        return;
      }

      try {
        const responses = await Promise.allSettled(
          accounts.map((acc) => api.get(`/members/${acc}`))
        );

        const detailsMap = {};
        responses.forEach((result, index) => {
          const acc = String(accounts[index]);
          detailsMap[acc] =
            result.status === "fulfilled" ? result.value.data : null;
        });

        setAccountDetailsMap(detailsMap);
      } catch (error) {
        console.error("Error fetching associated account details:", error);
      }
    };

    fetchAssociatedAccountDetails();
  }, [user]);

  useEffect(() => {
    const fetchMemberRelatedData = async () => {
      if (!selectedAccount || violationTypes.length === 0) return;

      try {
        let memberResData = null;

        try {
          const memberRes = await api.get(`/members/${selectedAccount}`);
          memberResData = memberRes.data || null;
        } catch (memberError) {
          memberResData = null;
        }

        const requests = [];

        if (dashboardSettings.show_violations) {
          requests.push(
            api.get(`/getViolations`, {
              params: { account_id: selectedAccount },
            })
          );
        } else {
          requests.push(Promise.resolve({ data: [] }));
        }

        if (dashboardSettings.show_work_orders) {
          requests.push(
            api.get(`/getWorkOrders`, {
              params: { property_id: selectedAccount },
            })
          );
        } else {
          requests.push(Promise.resolve({ data: [] }));
        }

        const [violationsRes, workOrdersRes] = await Promise.all(requests);

        setMemberData(memberResData);

        const enrichedViolations = (violationsRes.data || []).map((v) => {
          const type = violationTypes.find((t) => t.id === v.ccr_violation_type_id);
          return {
            ...v,
            type: type?.title || "N/A",
            description: type?.resolution_step || "No description",
          };
        });

        setViolations(enrichedViolations);
        setWorkOrders(workOrdersRes.data || []);
      } catch (error) {
        console.error("Error fetching account-related data:", error);
      }
    };

    fetchMemberRelatedData();
  }, [selectedAccount, violationTypes, dashboardSettings]);

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!dashboardSettings.show_documents) {
        setDocuments([]);
        return;
      }

      try {
        const res = await api.get(`/owner-documents`);
        setDocuments(res.data || []);
      } catch (error) {
        console.error("Error loading documents:", error);
      }
    };

    fetchDocuments();
  }, [dashboardSettings.show_documents]);

  const handleDownload = (doc) => {
    const url = `${API_BASE_URL}/uploads/${doc.stored_filename}`;
    window.location.href = url;
  };

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const statCardSx = {
    ...pageCardSx,
    p: 2.25,
    height: "100%",
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 700,
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const documentsByCategory = useMemo(() => {
    return Object.entries(
      documents.reduce((acc, doc) => {
        const category = doc.category_name || "Uncategorized";
        if (!acc[category]) acc[category] = [];
        acc[category].push(doc);
        return acc;
      }, {})
    );
  }, [documents]);

  const workOrderOpenCount = useMemo(() => {
    return workOrders.filter(
      (w) =>
        !["resolved", "closed", "completed"].includes(
          String(w.status || "").toLowerCase()
        )
    ).length;
  }, [workOrders]);

  const violationOpenCount = useMemo(() => {
    return violations.filter(
      (v) =>
        !["closed", "violation cured", "withdrawn / canceled"].includes(
          String(v.status || "").toLowerCase()
        )
    ).length;
  }, [violations]);

  const recentWorkOrders = useMemo(() => {
    return [...workOrders]
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
      .slice(0, 5);
  }, [workOrders]);

  const recentViolations = useMemo(() => {
    return [...violations]
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
      .slice(0, 5);
  }, [violations]);

  const summaryCards = useMemo(
    () =>
      [
        dashboardSettings.show_work_orders && {
          title: "Open Work Orders",
          value: workOrderOpenCount,
          subtitle: `${workOrders.length} total`,
          icon: <AssignmentIcon sx={{ color: "#2f63ad" }} />,
        },
        dashboardSettings.show_violations && {
          title: "Open Violations",
          value: violationOpenCount,
          subtitle: `${violations.length} total`,
          icon: <WarningAmberIcon sx={{ color: "#d97706" }} />,
        },
        dashboardSettings.show_documents && {
          title: "Documents",
          value: documents.length,
          subtitle: `${documentsByCategory.length} categories`,
          icon: <InsertDriveFileIcon sx={{ color: "#2f63ad" }} />,
        },
        {
          title: "Account",
          value: getDisplayTitle(memberData, selectedProperty, selectedAccount || "?"),
          subtitle: `Code: ${selectedAccount || "-"}`,
          icon: <HomeWorkIcon sx={{ color: "#2f63ad" }} />,
        },
      ].filter(Boolean),
    [
      dashboardSettings,
      workOrderOpenCount,
      workOrders.length,
      violationOpenCount,
      violations.length,
      documents.length,
      documentsByCategory.length,
      memberData,
      selectedProperty,
      selectedAccount,
    ]
  );

  const renderSimpleInsightCard = (title, items, emptyText, formatter) => (
    <Card sx={{ ...pageCardSx, p: 2.5, height: "100%" }}>
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
        Quick recent activity overview.
      </Typography>

      {items.length === 0 ? (
        <Typography variant="body2" sx={{ color: "#6b778c" }}>
          {emptyText}
        </Typography>
      ) : (
        <Stack spacing={1.25}>
          {items.map((item, idx) => (
            <Paper
              key={idx}
              elevation={0}
              sx={{
                p: 1.25,
                borderRadius: "12px",
                border: "1px solid rgba(47,99,173,0.08)",
                backgroundColor: "rgba(47,99,173,0.02)",
              }}
            >
              {formatter(item)}
            </Paper>
          ))}
        </Stack>
      )}
    </Card>
  );

  const renderDocumentsPanel = () => (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
        HOA Documents
      </Typography>
      <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
        Browse uploaded files by category.
      </Typography>

      {documents.length === 0 ? (
        <Box
          sx={{
            minHeight: 220,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            textAlign: "center",
            color: "grey.500",
          }}
        >
          <InsertDriveFileIcon sx={{ fontSize: 58, mb: 1.5, color: "grey.400" }} />
          <Typography variant="body2" color="text.secondary">
            No documents uploaded yet.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.25}>
          {documentsByCategory.map(([category, docs]) => (
            <Paper
              key={category}
              elevation={0}
              sx={{
                borderRadius: "12px",
                border: "1px solid rgba(47,99,173,0.08)",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  px: 1.5,
                  py: 1.25,
                  backgroundColor: "rgba(47,99,173,0.04)",
                  borderBottom: "1px solid rgba(47,99,173,0.08)",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {category} ({docs.length})
                </Typography>
              </Box>

              <Box sx={{ p: 1.25 }}>
                {docs.slice(0, 5).map((doc) => {
                  const getIcon = () => {
                    if (doc.file_type?.includes("pdf")) {
                      return <DescriptionIcon fontSize="small" sx={{ color: "#2f63ad" }} />;
                    }
                    if (doc.file_type?.includes("image")) {
                      return <ImageIcon fontSize="small" sx={{ color: "#2e7d32" }} />;
                    }
                    return (
                      <InsertDriveFileIcon fontSize="small" sx={{ color: "#6b778c" }} />
                    );
                  };

                  return (
                    <Box
                      key={doc.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        border: "1px solid #eee",
                        borderRadius: 1.5,
                        p: 1,
                        mb: 0.75,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          boxShadow: 2,
                          borderColor: "rgba(47,99,173,0.22)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flex: 1,
                          gap: 1,
                          minWidth: 0,
                        }}
                      >
                        {getIcon()}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Tooltip title={doc.original_filename}>
                            <Typography variant="body2" noWrap fontWeight={600}>
                              {doc.description || doc.original_filename}
                            </Typography>
                          </Tooltip>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {new Date(doc.upload_date).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Box>

                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Preview">
                          <IconButton size="small" onClick={() => setPreviewFile(doc)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Download">
                          <IconButton size="small" onClick={() => handleDownload(doc)}>
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>
                  );
                })}

                {docs.length > 5 && (
                  <Button
                    size="small"
                    variant="text"
                    href="/owner-documents"
                    startIcon={<FolderOpenIcon />}
                    sx={{ mt: 0.5, textTransform: "none", fontWeight: 600 }}
                  >
                    View all documents
                  </Button>
                )}
              </Box>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );

  const activityTabs = useMemo(() => {
    const tabs = [];

    if (dashboardSettings.show_violations) {
      tabs.push({
        key: "violations",
        label: "Violations",
        content: <ViolationSummary violations={violations} />,
      });
    }

    if (dashboardSettings.show_work_orders) {
      tabs.push({
        key: "work_orders",
        label: "Work Orders",
        content: <WorkOrders workOrders={workOrders} propertyId={selectedAccount} />,
      });
    }

    if (dashboardSettings.show_architectural_requests) {
      tabs.push({
        key: "architectural_requests",
        label: "Architectural Requests",
        content: (
          <ArchitecturalRequests
            accountId={selectedAccount}
            propertyId={selectedAccount}
          />
        ),
      });
    }

    if (dashboardSettings.show_documents) {
      tabs.push({
        key: "documents",
        label: "Documents",
        content: renderDocumentsPanel(),
      });
    }

    return tabs;
  }, [
    dashboardSettings,
    violations,
    workOrders,
    selectedAccount,
    documents,
    documentsByCategory,
  ]);

  useEffect(() => {
    if (activeTab >= activityTabs.length) {
      setActiveTab(0);
    }
  }, [activityTabs, activeTab]);

  const showInsightsSection =
    dashboardSettings.show_work_orders ||
    dashboardSettings.show_violations ||
    dashboardSettings.show_documents;

  return (
    <Container
      maxWidth="xl"
      sx={{
        pt: { xs: "72px", md: "76px" },
        pb: { xs: 4, md: 6 },
        px: { xs: 1.5, sm: 2, md: 3 },
      }}
    >
      <Card
        sx={{
          ...pageCardSx,
          mb: 3,
          p: { xs: 2, md: 2.5 },
          background:
            "linear-gradient(180deg, rgba(47,99,173,0.05) 0%, rgba(255,255,255,0.96) 100%)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(47,99,173,0.08)",
              flexShrink: 0,
            }}
          >
            <HomeWorkIcon sx={{ color: "#2f63ad" }} />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              sx={{
                fontWeight: 700,
                color: "#223142",
                lineHeight: 1.15,
              }}
            >
              My Dashboard
            </Typography>

            <Typography variant="body1" sx={{ color: "#6b778c", mt: 0.5 }}>
              Welcome back, {user?.first_name || "Homeowner"}
            </Typography>
          </Box>
        </Box>
      </Card>

      {user?.AssociatedAccounts?.length > 1 && !selectedAccount && (
        <Card sx={{ ...pageCardSx, p: { xs: 2, md: 7 }, mb: 4 }}>
          <Box sx={{ mb: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(47,99,173,0.08)",
                }}
              >
                <HomeWorkIcon sx={{ color: "#2f63ad" }} />
              </Box>

              <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
                Select an Account
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              Choose a property account to view documents, work orders, violations,
              and activity.
            </Typography>
          </Box>

          <Grid container spacing={2.5}>
            {user.AssociatedAccounts.map((acc) => {
              const accountKey = String(acc);
              const member = accountDetailsMap[accountKey];
              const property = propertiesMap[accountKey];

              const displayTitle = getDisplayTitle(member, property, accountKey);
              const displaySubtitle = getDisplaySubtitle(member, property, accountKey);

              return (
                <Grid item xs={12} sm={6} md={4} key={accountKey}>
                  <Card
                    onClick={() => setSelectedAccount(accountKey)}
                    sx={{
                      borderRadius: "16px",
                      border: "1px solid rgba(47,99,173,0.08)",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                      backgroundColor: "rgba(255,255,255,0.96)",
                      p: 2.5,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      height: "100%",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 12px 30px rgba(0,0,0,0.1)",
                        borderColor: "rgba(47,99,173,0.18)",
                        backgroundColor: "rgba(47,99,173,0.02)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 1.5,
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.75 }}>
                          Property Account
                        </Typography>

                        <Typography
                          variant="h6"
                          sx={{
                            color: "#223142",
                            fontWeight: 700,
                            lineHeight: 1.2,
                            wordBreak: "break-word",
                          }}
                        >
                          {displayTitle}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color: "#6b778c",
                            mt: 0.75,
                            wordBreak: "break-word",
                          }}
                        >
                          {displaySubtitle}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mt: 1,
                            color: "#2f63ad",
                            fontWeight: 700,
                            letterSpacing: "0.2px",
                          }}
                        >
                          Member Code: {accountKey}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "12px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "rgba(47,99,173,0.08)",
                          flexShrink: 0,
                        }}
                      >
                        <HomeWorkIcon sx={{ color: "#2f63ad" }} />
                      </Box>
                    </Box>

                    <Button
                      variant="outlined"
                      sx={{ ...secondaryButtonSx, mt: 2.25 }}
                      fullWidth
                    >
                      Open Dashboard
                    </Button>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Card>
      )}

      {selectedAccount && (
        <>
          {user?.AssociatedAccounts?.length > 1 && (
            <Box sx={{ mb: 2.5 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => setSelectedAccount(null)}
                sx={secondaryButtonSx}
              >
                Back to Account Selection
              </Button>
            </Box>
          )}

          <Card sx={{ ...pageCardSx, p: { xs: 1.75, sm: 2, md: 2.5 }, mb: 3 }}>
            <ProfileCard user={{ ...user, ...memberData }} />
          </Card>

          {summaryCards.length > 0 && (
            <Grid container spacing={2.5} sx={{ mb: { xs: 3, md: 6 } }}>
              {summaryCards.map((card) => (
                <Grid item xs={12} sm={6} md={3} key={card.title}>
                  <Card sx={statCardSx}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: 1.5,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.75 }}>
                          {card.title}
                        </Typography>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            color: "#223142",
                            mb: 0.5,
                            wordBreak: "break-word",
                          }}
                        >
                          {card.value}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#8a94a6" }}>
                          {card.subtitle}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: "12px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "rgba(47,99,173,0.08)",
                          flexShrink: 0,
                        }}
                      >
                        {card.icon}
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          <Card sx={{ ...pageCardSx, overflow: "hidden", mb: 4 }}>
            <Box
              sx={{
                px: { xs: 1.5, sm: 2, md: 4 },
                pt: 2.25,
                pb: 1.25,
                borderBottom: "1px solid rgba(47,99,173,0.08)",
                backgroundColor: "rgba(47,99,173,0.02)",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#223142",
                  mb: 0.5,
                }}
              >
                Account Activity
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "#6b778c",
                  mb: 2,
                }}
              >
                Review available account sections.
              </Typography>

              {activityTabs.length > 0 ? (
                <Tabs
                  value={activeTab}
                  onChange={(_, value) => setActiveTab(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    minHeight: "auto",
                    "& .MuiTab-root": {
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "10px",
                      minHeight: 40,
                      color: "#5f6b7a",
                      mr: 1,
                      px: { xs: 1.5, md: 2 },
                      py: 1,
                      whiteSpace: "nowrap",
                    },
                    "& .Mui-selected": {
                      color: "#2f63ad !important",
                      backgroundColor: "rgba(47,99,173,0.08)",
                    },
                    "& .MuiTabs-indicator": {
                      display: "none",
                    },
                  }}
                >
                  {activityTabs.map((tab) => (
                    <Tab key={tab.key} label={tab.label} />
                  ))}
                </Tabs>
              ) : null}
            </Box>

            <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, overflowX: "hidden" }}>
              {activityTabs.length > 0 ? (
                activityTabs[activeTab]?.content
              ) : (
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  No sections are available right now.
                </Typography>
              )}
            </Box>
          </Card>

          {showInsightsSection && (
            <Grid container spacing={2.5} sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <Card sx={{ ...pageCardSx, p: 2.5, height: "100%" }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
                  >
                    Quick Insights
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
                    Snapshot of current account activity.
                  </Typography>

                  <Stack spacing={1.25}>
                    {dashboardSettings.show_work_orders && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          borderRadius: "12px",
                          border: "1px solid rgba(47,99,173,0.08)",
                          backgroundColor: "rgba(47,99,173,0.02)",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <InsightsIcon sx={{ color: "#2f63ad", fontSize: 20 }} />
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: "#223142" }}
                          >
                            Open work orders: {workOrderOpenCount}
                          </Typography>
                        </Box>
                      </Paper>
                    )}

                    {dashboardSettings.show_violations && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          borderRadius: "12px",
                          border: "1px solid rgba(47,99,173,0.08)",
                          backgroundColor: "rgba(47,99,173,0.02)",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <WarningAmberIcon sx={{ color: "#d97706", fontSize: 20 }} />
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: "#223142" }}
                          >
                            Open violations: {violationOpenCount}
                          </Typography>
                        </Box>
                      </Paper>
                    )}

                    {dashboardSettings.show_documents && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          borderRadius: "12px",
                          border: "1px solid rgba(47,99,173,0.08)",
                          backgroundColor: "rgba(47,99,173,0.02)",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <ArchitectureIcon sx={{ color: "#2f63ad", fontSize: 20 }} />
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: "#223142" }}
                          >
                            Documents available: {documents.length}
                          </Typography>
                        </Box>
                      </Paper>
                    )}
                  </Stack>
                </Card>
              </Grid>

              {dashboardSettings.show_work_orders && (
                <Grid item xs={12} md={4}>
                  {renderSimpleInsightCard(
                    "Recent Work Orders",
                    recentWorkOrders,
                    "No recent work orders found.",
                    (item) => (
                      <>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#223142" }}
                        >
                          {item.title || item.description || `Work Order #${item.id}`}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#6b778c" }}>
                          {item.status || "Unknown status"}
                        </Typography>
                      </>
                    )
                  )}
                </Grid>
              )}

              {dashboardSettings.show_violations && (
                <Grid item xs={12} md={4}>
                  {renderSimpleInsightCard(
                    "Recent Violations",
                    recentViolations,
                    "No recent violations found.",
                    (item) => (
                      <>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#223142" }}
                        >
                          {item.type || "Violation"}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#6b778c" }}>
                          {item.status || "Unknown status"}
                        </Typography>
                      </>
                    )
                  )}
                </Grid>
              )}
            </Grid>
          )}

          <Modal open={Boolean(previewFile)} onClose={() => setPreviewFile(null)}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: { xs: "94%", md: "80%" },
                maxWidth: 960,
                maxHeight: "90vh",
                bgcolor: "background.paper",
                borderRadius: "18px",
                boxShadow: 24,
                p: { xs: 2, md: 3 },
                overflow: "auto",
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {previewFile?.original_filename}
                </Typography>
                <IconButton onClick={() => setPreviewFile(null)}>
                  <CloseIcon />
                </IconButton>
              </Box>

              {previewFile?.file_type === "application/pdf" ? (
                <iframe
                  src={`${API_BASE_URL}${previewFile.file_path}`}
                  title="PDF Preview"
                  width="100%"
                  height="600px"
                  style={{ border: "none", borderRadius: "8px" }}
                />
              ) : previewFile?.file_type === "text/plain" ? (
                <iframe
                  src={`${API_BASE_URL}${previewFile.file_path}`}
                  title="Text Preview"
                  width="100%"
                  height="600px"
                  style={{
                    border: "1px solid #ddd",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "8px",
                  }}
                />
              ) : previewFile?.original_filename?.toLowerCase().endsWith(".docx") ? (
                <Typography variant="body2" color="text.secondary">
                  DOCX preview not supported.{" "}
                  <a
                    href={`${API_BASE_URL}${previewFile.file_path}`}
                    download={previewFile.original_filename}
                  >
                    Download the file
                  </a>{" "}
                  to view.
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Cannot preview this file type. Please download it to view.
                </Typography>
              )}
            </Box>
          </Modal>
        </>
      )}
    </Container>
  );
};

export default OwnerDashboard;